import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import DateFormat from '../../store/types/enums/DateFormat'
import parseJsonSafe from '../../components/Schedule/parseJsonSafe'
import { History } from '../../store/redux/reducers/auditSlice'
import HistoryContentRows from './HistoryContentRows'

interface WorkHoursExpandableHistoryRowProps {
  data: History
  acronyms: any[] | undefined
}

export const WorkHoursExpandableHistoryRow: React.FC<WorkHoursExpandableHistoryRowProps> = ({ data, acronyms }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const oldData = useMemo(() => parseJsonSafe(data.oldValue), [data.oldValue])
  const newData = useMemo(() => parseJsonSafe(data.newValue), [data.newValue])
  const isAddition = Object.keys(oldData).length === 0 && Object.keys(newData).length > 0
  const isDeletion = Object.keys(oldData).length > 0 && Object.keys(newData).length === 0
  const actionLabel = t(`page.modification.${isAddition ? 'addition' : isDeletion ? 'deletion' : 'modification'}`)

  const dataSource = isAddition ? newData : oldData

  const actionColor = isAddition
    ? theme.palette.success.main
    : isDeletion
    ? theme.palette.error.main
    : theme.palette.info.main

  let projectDisplay
  if (acronyms === undefined) projectDisplay = ' '
  else if (
    !isAddition &&
    !isDeletion &&
    oldData?.project_id &&
    newData?.project_id &&
    oldData.project_id !== newData.project_id
  ) {
    const matchingOldProject = acronyms?.find(item => item.id === oldData.project_id)
    const matchingNewProject = acronyms?.find(item => item.id === newData.project_id)
    const oldProject = matchingOldProject?.acronym || oldData.project_id
    const newProject = matchingNewProject?.acronym || newData.project_id
    projectDisplay = `${oldProject} -> ${newProject}`
  } else {
    const projId = dataSource?.project_id
    if (projId) {
      const matchingProject = acronyms?.find(item => item.id === projId)
      projectDisplay = matchingProject?.acronym || projId
    }
  }

  const projectRow = projectDisplay && (
    <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {t('page.modification.project')}
      </Typography>
      <Typography variant="body2">{projectDisplay}</Typography>
    </Stack>
  )

  return (
    <Box sx={{ mb: 2, borderRadius: 1, bgcolor: 'background.elevation2', p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Typography variant="body1">{data.name}</Typography>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: actionColor }}>
            {actionLabel}
          </Typography>
          <Typography variant="body2">{dayjs(data.createdAt).format(DateFormat.HourMinute)}</Typography>
        </Stack>
      </Stack>
      {projectRow}
      <HistoryContentRows oldData={oldData} newData={newData} isAddition={isAddition} isDeletion={isDeletion} t={t} />
    </Box>
  )
}
